import { AfterViewInit, Component, OnDestroy, OnInit } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { ActivatedRoute } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { Chart } from 'angular-highcharts';
import dayjs from 'dayjs/esm';
import { combineLatest } from 'rxjs';
import {
  CommonService,
  EnergyService,
  PlantServiceService,
  SiteService
} from '../../../../services/index';
import { Constants, Highcharts$ } from '../../../../shared/index';
import { EnergyTrendsComponent } from '../energy-trends/energy-trends.component';

@Component({
  selector: 'app-energy',
  templateUrl: './energy.component.html',
  styleUrls: ['./energy.component.scss']
})
export class EnergyComponent implements OnInit, AfterViewInit, OnDestroy {
  dateRange: any;
  alwaysShowCalendars: boolean;
  ranges: any = {
    // 'Today': [Moment$(), Moment$()],
    Yesterday: [dayjs().subtract(1, 'days'), dayjs().subtract(1, 'days')],
    'Last 7 Days': [dayjs().subtract(6, 'days'), dayjs()],
    'Last 30 Days': [dayjs().subtract(29, 'days'), dayjs()],
    'This Month': [dayjs().startOf('month'), dayjs().toDate()]
    // 'Last Month': [Moment$().subtract(1, 'month').startOf('month'), Moment$().subtract(1, 'month').endOf('month')]
  };
  totalEnergyConsumptionChart: Chart;
  energySourcingChart: Chart;
  topEnergyConsumersChart: Chart;
  demandSupplyTrendsChart: Chart;
  totalEnergyChartOption: any;
  processEnergyOption: any;
  demandSupplyTrendsChartOption: any;
  siteId: string;
  startDate: string;
  endDate: string;
  isFirstLoaded = true;
  isDateChanged = false;
  generatedTimeMsg = '';
  querySiteId: object;
  colors = ['#26b24a', '#ffe400', '#39c0ff'];
  totalEnergyConsumptionValue = 'NA';
  energyConsumptionSum: number;
  energyConsumptionValues: String[] = [];
  energyConsumptionCategories: String[] = [];
  totalEnergySourcingValue = 'NA';
  energySourcingSum: number;
  energySourcingValues: String[] = [];
  energySourcingCategories: String[] = [];
  isNotViewFull = true;
  buttons: any;
  isOnViewFull = false;
  yesterdayString: String;
  energyloss = {
    supplied: '',
    loss: '',
    metered: ''
  };
  langData: any;
  firstShiftTime: string;
  secondShiftTime: string;
  firstShiftName: string;
  secondShiftName: string;
  peak: string;
  energyConsumptionTooltip: string;
  energySourcingTooltip: string;
  energyLossTooltip: string;
  demandSupplyTooltip: string;
  topEnergyConsumTooltip: string;

  constructor(
    private energyService: EnergyService,
    private commonService: CommonService,
    private siteService: SiteService,
    private plantService: PlantServiceService,
    private activeRoute: ActivatedRoute,
    public dialog: MatDialog,
    public translate: TranslateService
  ) {
    this.translate.stream('energy').subscribe((subStream: any) => {
      this.langData = subStream;
    });
    this.peak = this.langData?.peak;
    this.energyConsumptionTooltip =
      this.langData?.dashboard?.energyConsumpTooltip;
    this.energySourcingTooltip =
      this.langData?.dashboard?.energySourcingTooltip;
    this.energyLossTooltip = this.langData?.dashboard?.energyLossTooltip;
    this.demandSupplyTooltip = this.langData?.dashboard?.demandSupplyTooltip;
    this.topEnergyConsumTooltip =
      this.langData?.dashboard?.topEnergyConsumTooltip;
    this.totalEnergyChartOption = this.getTotalEnergyChartOption();
    this.processEnergyOption = this.getProcessEnergyChartOption();
    this.totalEnergyConsumptionChart = new Chart(this.totalEnergyChartOption);
    //
    this.yesterdayString = `Yesterday (${dayjs()
      .subtract(1, 'days')
      .format(Constants.MOMENT_DATE_FORMAT.DAY_FORMAT)})`;
    // Testing
    this.energySourcingChart = new Chart(this.totalEnergyChartOption);
    this.topEnergyConsumersChart = new Chart(this.processEnergyOption);
    // Testing
    this.demandSupplyTrendsChartOption = this.getDemandSupplyTrendsChartOption(
      this.peak
    );
    this.alwaysShowCalendars = true;
    // this.dateRange = { 'startDate': '2019-07-17T07:01:39.547Z', 'endDate': '2019-07-17T07:01:39.547Z' };
    this.dateRange = {
      startDate: dayjs().subtract(1, 'days'),
      endDate: dayjs().subtract(1, 'days')
    };
  }

  ngOnInit() {
    const urlParams = combineLatest(
      this.activeRoute.params,
      this.activeRoute.queryParams,
      (params, queryParams) => ({ ...params, ...queryParams })
    );
    // Subscribe to the single observable, giving us both
    urlParams.subscribe((routeParams) => {
      this.siteId = routeParams.siteId;
      this.querySiteId = Object.assign({}, { siteId: this.siteId });
      this.getDemandSupplyTrends();
      this.processDataAndDrawChart();
    });
  }

  ngAfterViewInit() {
    const seft = this;
    this.buttons = Highcharts$.getOptions().exporting.buttons;
    Highcharts$.setOptions({
      exporting: {
        buttons: {
          contextButton: {
            menuItems: this.buttons.contextButton.menuItems.slice(0, 2)
          }
        }
      }
    });
    // document.addEventListener('resize', function () {
    window.onresize = function () {
      const dom = document.getElementById('chartContainer');
      if (seft.commonService.isNullOrUndefined(dom)) {
        return;
      }
      if (dom && dom.clientHeight > 400) {
        setTimeout(() => {
          dom.setAttribute('height', '400');
        }, 200);
      }
      if (document.fullscreenElement) {
        seft.demandSupplyTrendsChart.ref.setSize(
          document.documentElement.clientWidth,
          document.documentElement.clientHeight
        );
        seft.topEnergyConsumersChart.ref.setSize(
          document.documentElement.clientWidth,
          document.documentElement.clientHeight
        );
      } else {
        seft.demandSupplyTrendsChart.ref.setSize(
          dom.clientWidth,
          dom.clientHeight < 200 ? dom.clientHeight : 400
        );
        seft.topEnergyConsumersChart.ref.setSize(
          dom.clientWidth,
          dom.clientHeight < 200 ? dom.clientHeight : 400
        );
      }
    };
    // window.onresize = this.reportWindowSize;

    document.addEventListener('fullscreenchange', function () {
      seft.isOnViewFull = !seft.isOnViewFull;
      if (seft.demandSupplyTrendsChart.ref) {
        seft.demandSupplyTrendsChart.ref.reflow(new Event('resize'));
        // const dom = document.getElementById('demand');
        // seft.processEnergyConsumptionHCChart_Test.ref.setSize(dom.clientWidth, dom.clientHeight);

        seft.demandSupplyTrendsChart.ref.update({
          exporting: {
            buttons: {
              contextButton: {
                menuItems: seft.isNotViewFull
                  ? seft.buttons.contextButton.menuItems.slice(1, 2)
                  : seft.buttons.contextButton.menuItems.slice(0, 2)
              }
            }
          }
        });
      }
      if (seft.topEnergyConsumersChart.ref) {
        seft.topEnergyConsumersChart.ref.update({
          exporting: {
            buttons: {
              contextButton: {
                menuItems: seft.isNotViewFull
                  ? seft.buttons.contextButton.menuItems.slice(1, 2)
                  : seft.buttons.contextButton.menuItems.slice(0, 2)
              }
            }
          }
        });
      }
      seft.isNotViewFull = !seft.isNotViewFull;
    });
  }

  ngOnDestroy() {
    document.removeEventListener('fullscreenchange', function () {});
  }

  reportWindowSize() {
    const dom = document.getElementById('chartContainer');
    this.demandSupplyTrendsChart.ref.setSize(
      dom.clientWidth,
      dom.clientHeight < 400 ? dom.clientHeight : 400
    );
  }

  datesUpdated(event) {}

  public onChangeDateRange = () => {
    if (
      dayjs(this.dateRange.endDate).diff(
        dayjs(this.dateRange.startDate),
        'days'
      ) >= 31
    ) {
      this.commonService.showDialogMessage(
        this.translate.instant(Constants.DATE_LIMIT_31_DAYS)
      );
      return;
    }

    if (this.commonService.isLaterThanCurentDate(this.dateRange.endDate)) {
      this.commonService.showDialogMessage(
        this.translate.instant(Constants.FUTURE_DATE_NOT_ALLOW)
      );
      return;
    }

    if (
      this.isFirstLoaded === false &&
      !this.commonService.isNullOrUndefined(this.siteId)
    ) {
      this.processDataAndDrawChart();
    }
  };

  /**
   * Method to process data and draw chart for the selected site within a date range
   *
   * @memberof EnergyComponent
   */
  private processDataAndDrawChart = () => {
    const seft = this;
    this.transformDate();
    this.isFirstLoaded = false;
    this.energyService
      .getShiftwiseenergy(this.siteId, this.startDate, this.endDate)
      .subscribe((data) => {
        const chartData = [];
        let total = 0;
        if (data.data !== undefined) {
          for (let i = 0; i < data.data.length; i++) {
            total += data.data[i];
            chartData.push([data.categories[i], data.data[i]]);
          }
          this.energyConsumptionSum = data.sum;
          this.energyConsumptionValues = data.data;
          this.energyConsumptionCategories = data.categories;
        }
        this.totalEnergyConsumptionValue =
          Highcharts$.numberFormat(
            total,
            Constants.DECIMAL_FORMAT.ZERO_DECIMAL,
            '.',
            ','
          ) + ' kWh';
        this.totalEnergyChartOption.series[0].data = chartData;
        this.totalEnergyConsumptionChart = new Chart(
          this.totalEnergyChartOption
        );
      });
    this.energyService
      .getEnergyPareto(this.siteId, this.startDate, this.endDate)
      .subscribe((data) => {
        if (data instanceof Array) {
          const result = data.map((x) => [x.processName, x.energyValue]);
          this.processEnergyOption.series[0].data = result;
          this.topEnergyConsumersChart = new Chart(this.processEnergyOption);
        }
      });
    this.energyService
      .getEnergySourcingShiftWise(this.siteId, this.startDate, this.endDate)
      .subscribe((res) => {
        const chartData = [];
        let total = 0;
        if (res.data !== undefined) {
          for (let i = 0; i < res.data.length; i++) {
            total += res.data[i];
            chartData.push([res.categories[i], res.data[i]]);
          }
          this.energySourcingSum = res.sum;
          this.energySourcingValues = res.data;
          this.energySourcingCategories = res.categories;
        }

        this.totalEnergySourcingValue =
          Highcharts$.numberFormat(
            total,
            Constants.DECIMAL_FORMAT.ZERO_DECIMAL,
            '.',
            ','
          ) + ' kWh';
        this.totalEnergyChartOption.series[0].data = chartData;
        this.energySourcingChart = new Chart(this.totalEnergyChartOption);
      });
    this.energyService
      .getEnergyLoss(this.siteId, this.startDate, this.endDate)
      .subscribe((res) => {
        if (!this.commonService.isNullOrUndefined(res)) {
          seft.energyloss.loss = res.loss.toFixed(2);
          seft.energyloss.supplied = res.supplied.toFixed(2);
          seft.energyloss.metered = res.metered.toFixed(2);
        }
      });
    this.plantService.getShiftsBySiteId(this.siteId).subscribe((data) => {
      if (data.length > 0) {
        const firstShiftStart = data[0].shiftStart;
        const firstShiftEnd = data[0].shiftEnd;
        const lastShiftStart = data[data.length - 1].shiftStart;
        const lastShiftEnd = data[data.length - 1].shiftEnd;
        this.firstShiftTime =
          firstShiftStart.substring(
            firstShiftStart.indexOf('T') + 1,
            firstShiftStart.indexOf('.')
          ) +
          ' - ' +
          firstShiftEnd.substring(
            firstShiftEnd.indexOf('T') + 1,
            firstShiftEnd.indexOf('.')
          );
        this.secondShiftTime =
          lastShiftStart.substring(
            lastShiftStart.indexOf('T') + 1,
            lastShiftStart.indexOf('.')
          ) +
          ' - ' +
          lastShiftEnd.substring(
            lastShiftEnd.indexOf('T') + 1,
            lastShiftEnd.indexOf('.')
          );
        this.firstShiftName = data[0].shiftName;
        this.secondShiftName = data[data.length - 1].shiftName;
        const dayRange = dayjs(this.endDate).diff(
          dayjs(this.startDate),
          'days'
        );
        let newDate;
        if (dayRange === 0) {
          newDate = dayjs(this.endDate).add(1, 'days');
        } else {
          newDate = dayjs(this.endDate);
        }
        this.generatedTimeMsg = `${this.translate.instant(
          Constants.GENERATED_TIME_MSG
        )} ${dayjs(this.startDate).format(
          Constants.MOMENT_DATE_FORMAT.DAY_FORMAT
        )}
            ${dayjs(firstShiftStart).format(
              Constants.MOMENT_DATE_FORMAT.HOURS_FORMAT
            )}
            ${this.translate.instant(Constants.TO)} ${dayjs(newDate).format(
          Constants.MOMENT_DATE_FORMAT.DAY_FORMAT
        )}
            ${dayjs(lastShiftEnd).format(
              Constants.MOMENT_DATE_FORMAT.HOURS_FORMAT
            )}`;
      }
    });
  };

  /**
   * Method to get Demand Supply Trends for the selected site for the current day
   *
   * @memberof EnergyComponent
   */
  getDemandSupplyTrends() {
    const startDate = dayjs().format(Constants.MOMENT_DATE_FORMAT.DATE_FORMAT);
    const endDate = dayjs().format(Constants.MOMENT_DATE_FORMAT.DATE_FORMAT);

    this.siteService
      .getDemandSupplyTrendsChart(this.siteId, startDate, endDate)
      .subscribe((data) => {
        if (
          !this.commonService.isNullOrUndefined(data) &&
          (Array.isArray(data.demand) || Array.isArray(data.supply))
        ) {
          const demandData = data?.demand?.map((v) => [v.x, v.y]);
          const demand = {
            name: this.langData.demand,
            data: demandData
          };
          const supply_tepcoData = data.supply_tepco.map((v) => [v.x, v.y]);
          const supply_tepco = {
            name: this.langData.supplyTepco,
            data: supply_tepcoData
          };
          const supply_gasengineData = data.supply_gasengine.map((v) => [
            v.x,
            v.y
          ]);
          const supply_gasengine = {
            name: this.langData.supplyGasEngine,
            data: supply_gasengineData
          };
          const peakPower = { name: this.langData.peakPower, data: null };
          const series = [supply_tepco, supply_gasengine, demand, peakPower];

          const peakTepco = this.langData.supplyTepco;
          const peakDemand = this.langData.demand;
          const peakGasEngine = this.langData.supplyGasEngine;

          const event = {
            load: function () {
              this.series.forEach((ser) => {
                ser.points.forEach((point) => {
                  let isPeakDemand, isPeakTepco, isPeakGasengine;

                  switch (ser.name) {
                    case peakTepco: {
                      if (data.peak_supply_tepco[0].y === 0) {
                        isPeakTepco = false;
                      } else {
                        isPeakTepco = point.y === data?.peak_supply_tepco[0]?.y;
                      }
                      break;
                    }
                    case peakDemand: {
                      if (data.peak_demand[0].y === 0) {
                        isPeakDemand = false;
                      } else {
                        isPeakDemand = point.y === data?.peak_demand[0]?.y;
                      }
                      break;
                    }
                    case peakGasEngine: {
                      if (data.peak_supply_gasengine[0].y === 0) {
                        isPeakGasengine = false;
                      } else {
                        isPeakGasengine =
                          point.y === data.peak_supply_gasengine[0].y;
                      }
                      break;
                    }
                    default:
                  }

                  if (isPeakTepco || isPeakDemand || isPeakGasengine) {
                    point.update(
                      {
                        color: '#EA0016',
                        marker: {
                          enabled: true,
                          radius: 5,
                          states: {
                            hover: {
                              radius: 10
                            }
                          }
                        }
                      },
                      false
                    );
                  }
                });
              });

              this.redraw();
            }
          };

          this.demandSupplyTrendsChartOption.chart.events = event;
          this.demandSupplyTrendsChartOption.series = series;
          this.demandSupplyTrendsChart = new Chart(
            this.demandSupplyTrendsChartOption
          );
        }
      });
  }

  private transformDate = () => {
    this.startDate = dayjs(this.dateRange.startDate).format(
      Constants.MOMENT_DATE_FORMAT.DATE_FORMAT
    );
    this.endDate = dayjs(this.dateRange.endDate).format(
      Constants.MOMENT_DATE_FORMAT.DATE_FORMAT
    );
  };

  // New REQ
  private getTotalEnergyChartOption = () => {
    const option = {
      chart: {
        type: 'pie',
        plotBackgroundColor: null,
        plotBorderWidth: 0,
        plotShadow: false,
        height: '80%'
      },
      title: {
        text: ''
      },
      subtitle: {
        text: '',
        align: 'center'
        // y : 40
      },
      tooltip: {
        formatter: function () {
          return (
            '<b>' +
            this.point.name +
            '<br/><span style="color:' +
            this.series.points[this.point.index].color +
            '">\u25CF</span>   ' +
            this.series.name +
            ': ' +
            Highcharts$.numberFormat(
              this.y,
              Constants.DECIMAL_FORMAT.ZERO_DECIMAL,
              '.',
              ','
            ) +
            ' kWh (' +
            Highcharts$.numberFormat(
              this.percentage,
              Constants.DECIMAL_FORMAT.ZERO_DECIMAL
            ) +
            '%)</b>'
          );
        }
      },
      plotOptions: {
        pie: {
          dataLabels: {
            enabled: true,
            distance: -20,
            style: {},
            formatter: function () {
              return (
                Highcharts$.numberFormat(
                  this.point.percentage,
                  Constants.DECIMAL_FORMAT.ZERO_DECIMAL
                ) + ' %'
              );
            }
          },
          startAngle: -180,
          endAngle: 180,
          size: '100%',
          showInLegend: false
        }
      },
      credits: {
        enabled: false
      },
      exporting: {
        enabled: false
      },
      colors: this.colors,
      legend: {
        align: 'center',
        verticalAlign: 'bottom'
      },
      series: [
        {
          type: 'pie',
          name: this.langData?.energy,
          innerSize: '60%',
          data: []
        }
      ]
    };
    return option;
  };

  /**
   * Method to get Process Energy chart option
   *
   * @memberof EnergyComponent
   */
  private getProcessEnergyChartOption = () => {
    const option = {
      chart: {
        type: 'column'
      },
      title: {
        text: ''
      },
      subtitle: {
        text: '',
        align: 'center'
        // y : 40
      },
      xAxis: {
        type: 'category',
        labels: {
          style: {
            fontWeight: 'bold',
            fontSize: 12
          }
        }
      },
      yAxis: {
        min: 0,
        title: {
          text: this.langData?.energyPareto,
          style: {
            fontWeight: 'bold',
            fontSize: 12
          }
        },
        stackLabels: {
          enabled: true,
          style: {
            fontWeight: 'bold',
            color: 'gray',
            textOutline: '0'
          },
          formatter: function () {
            return Highcharts$.numberFormat(
              this.axis.series[0].yData[this.x],
              Constants.DECIMAL_FORMAT.ZERO_DECIMAL,
              '.',
              ','
            );
          }
        },
        labels: {
          // format: '{value}',
          formatter: function () {
            return Highcharts$.numberFormat(
              this.value,
              Constants.DECIMAL_FORMAT.ZERO_DECIMAL,
              '.',
              ','
            );
          },
          style: {
            fontWeight: 'bold',
            fontSize: 12
          }
        }
      },
      legend: {
        align: 'right',
        x: -30,
        verticalAlign: 'top',
        y: 25,
        floating: true,
        backgroundColor: 'white',
        borderColor: '#CCC',
        borderWidth: 1,
        shadow: false
      },
      plotOptions: {
        column: {
          stacking: 'normal',
          dataLabels: {
            enabled: false,
            color: 'grey',
            style: {
              // fontWeight: 'bold',
              // fontSize: 12
            },
            formatter: function () {
              return Highcharts$.numberFormat(
                this.y,
                Constants.DECIMAL_FORMAT.ZERO_DECIMAL
              );
            }
          }
        }
      },
      tooltip: {
        formatter: function () {
          let dataSum = 0;
          const seriesData = this.series.data;
          for (let i = 0; i < seriesData.length; i++) {
            dataSum += seriesData[i].y;
          }
          const pcnt = (this.y / dataSum) * 100;
          // eslint-disable-next-line max-len
          return (
            '<b>' +
            this.key +
            '<br/>' +
            Highcharts$.numberFormat(
              pcnt,
              Constants.DECIMAL_FORMAT.ZERO_DECIMAL
            ) +
            ' %<br/>' +
            Highcharts$.numberFormat(
              this.y,
              Constants.DECIMAL_FORMAT.ZERO_DECIMAL,
              '.',
              ','
            ) +
            ' kWh</b>'
          );
        }
      },
      credits: {
        enabled: false
      },
      series: [
        {
          name: this.langData?.processEnergyConsumption,
          data: []
        }
      ]
    };
    return option;
  };

  /**
   * Method to get Demand Supply Trends chart option
   *
   * @memberof EnergyComponent
   */
  private getDemandSupplyTrendsChartOption = (peak: string) => {
    const option = {
      chart: {
        type: 'spline'
      },
      title: {
        text: ''
      },
      subtitle: {
        text: ''
      },
      credits: {
        enabled: false
      },
      navigator: {
        enabled: true
      },
      colors: ['#A791BF', '#7FC6E7', '#BBDE8F', '#EA0016'],
      xAxis: {
        type: 'datetime',
        labels: {
          autoRotation: [-45],
          formatter: function () {
            return Highcharts$.dateFormat('%H:%M', this.value);
          },
          style: {
            fontWeight: 'bold',
            fontSize: 12
          }
        },
        crosshair: true
      },
      yAxis: {
        title: {
          text: 'kW',
          style: {
            fontWeight: 'bold',
            fontSize: 12
          }
        },
        labels: {
          // format: '{value}',
          formatter: function () {
            return Highcharts$.numberFormat(
              this.value,
              Constants.DECIMAL_FORMAT.ZERO_DECIMAL,
              '.',
              ','
            );
          },
          style: {
            fontWeight: 'bold',
            fontSize: 12
          }
        },
        lineWidth: 2,
        min: 0
      },
      legend: {
        enabled: true
      },
      tooltip: {
        formatter: function () {
          const isPeak = this.point.color === '#EA0016';
          let peakPower = '';
          if (isPeak) {
            peakPower = peak;
          }
          return (
            '<b>' +
            peakPower +
            ' ' +
            this.series.name +
            '<br/>' +
            dayjs(this.point.x).format(
              Constants.MOMENT_DATE_FORMAT.FULL_DATE_TIME_FORMAT
            ) +
            ' : ' +
            Highcharts$.numberFormat(
              this.point.y,
              Constants.DECIMAL_FORMAT.ZERO_DECIMAL,
              '.',
              ','
            ) +
            ' kW</b>'
          );
        }
      },
      plotOptions: {
        series: {
          stacking: 'normal',
          connectNulls: true,
          marker: {
            enabled: false
          }
        }
      },
      series: []
    };
    return option;
  };

  /**
   * Method to open the Energy long term trends
   *
   * @memberof EnergyComponent
   */
  onEnergyTrendClick() {
    const dialogRef = this.dialog.open(EnergyTrendsComponent, {
      width: '95%',
      maxHeight: '670px',
      maxWidth: '95vw'
    });
    dialogRef.afterClosed().subscribe((data) => {
      if (data && data.data) {
        console.log(data.data);
      }
    });
  }
}
